import { useState } from "react";
import { Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/shared/components/ui/AlertDialog";
import { Button } from "@/shared/components/ui/Button";
import { useDeleteContacts } from "@/shared/lib/contacts";

interface DeleteSelectedButtonProps {
  selectedIds: string[];
  onDone: () => void;
}

/**
 * Bulk "delete contacts" action — destructive, so it confirms via an
 * AlertDialog before firing POST /contacts/bulk. On success the selection is
 * cleared (onDone) and the dialog closes.
 */
export function DeleteSelectedButton({
  selectedIds,
  onDone,
}: DeleteSelectedButtonProps) {
  const del = useDeleteContacts();
  const [open, setOpen] = useState(false);
  const count = selectedIds.length;

  function handleDelete() {
    del.mutate(selectedIds, {
      onSuccess: () => {
        setOpen(false);
        onDone();
      },
    });
  }

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button
          variant="ghost-danger"
          size="sm"
          disabled={count === 0}
          aria-label="Delete contacts"
        >
          <Trash2 aria-hidden className="h-4 w-4" />
          <span className="hidden sm:inline">Delete</span>
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            Delete {count} {count === 1 ? "contact" : "contacts"}?
          </AlertDialogTitle>
          <AlertDialogDescription>
            This permanently removes the selected{" "}
            {count === 1 ? "contact" : "contacts"} along with their tag links and
            list memberships. The action can't be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction asChild>
            <Button
              variant="destructive"
              onClick={(e) => {
                // Keep the dialog open until the mutation settles; onSuccess
                // closes it. Without this, AlertDialogAction auto-closes on click.
                e.preventDefault();
                handleDelete();
              }}
              disabled={del.isPending}
            >
              {del.isPending
                ? "Deleting…"
                : `Delete ${count === 1 ? "contact" : "contacts"}`}
            </Button>
          </AlertDialogAction>
          <AlertDialogCancel asChild>
            <Button variant="ghost">Cancel</Button>
          </AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
